/**
 * The Format tab's Number formatting vocabulary: the menu options the user
 * picks from, and the d3-format specifier each one writes into the spec.
 *
 * This lives in `services/` rather than the registry because it is half of the
 * spec contract, not styling: `readChartFormat` matches a spec's
 * `axis.format` back to an option here, and `applyChartFormatToSpec` writes the
 * specifier of whichever option was picked. The registry imports these for its
 * row config, so the menu labels and the spec mapping can never drift apart.
 *
 * `label` IS the stored value — `ChartFormatValues.currency` holds the sample
 * string, exactly as `colorPalette` holds a palette's name rather than its id.
 * Figma labels these options with a rendered sample (6409:556393, 6409:556404),
 * so the sample is what the field shows and what the menu lists.
 */
export interface ChartNumberFormatOption {
  id: string;
  /** Sample rendering of 5,000,000 (currency) or 0.125 (percentage). */
  label: string;
  /** d3-format specifier, written to `axis.format` / `text.format`. */
  format: string;
}

export const chartCurrencyFormatOptions: ChartNumberFormatOption[] = [
  { id: "currency-compact", label: "$5M", format: "$~s" },
  { id: "currency-compact-decimal", label: "$5.0M", format: "$.2s" },
  { id: "currency-grouped", label: "$5,000,000", format: "$,.0f" },
  { id: "currency-grouped-decimal", label: "$5,000,000.00", format: "$,.2f" },
];

/**
 * d3's `%` type multiplies by 100, so these suit a field holding a ratio
 * (0.125 → "12.5%"). A field already stored in percent units would read 1250%,
 * which is visible the moment the option is picked and undone by picking
 * another — the writer never applies a number format the user did not choose.
 */
export const chartPercentageFormatOptions: ChartNumberFormatOption[] = [
  { id: "percentage-whole", label: "13%", format: ".0%" },
  { id: "percentage-decimal", label: "12.5%", format: ".1%" },
  { id: "percentage-two-decimals", label: "12.50%", format: ".2%" },
];

/** Which `ChartFormatValues` key a given d3 specifier belongs to. */
export const numberFormatKey = (format: string): "currency" | "percentage" | undefined => {
  if (chartCurrencyFormatOptions.some((option) => option.format === format)) return "currency";
  if (chartPercentageFormatOptions.some((option) => option.format === format)) return "percentage";
  return undefined;
};

/** The menu label a spec's `format` string came from, if it came from one. */
export const numberFormatLabel = (format: string): string | undefined =>
  [...chartCurrencyFormatOptions, ...chartPercentageFormatOptions].find(
    (option) => option.format === format,
  )?.label;

/** The specifier behind a stored label — the inverse, used by the writer. */
export const numberFormatSpecifier = (label: string): string | undefined =>
  [...chartCurrencyFormatOptions, ...chartPercentageFormatOptions].find(
    (option) => option.label === label,
  )?.format;
